import { type SxProps, type Theme } from '@ltvco/refresh-lib/theme';
import React from 'react';

import { SEARCH_OPERATORS } from '../../constants';
import {
  SearchForm,
  type SearchFormData,
  type SearchTypeOption,
} from '../shared';

import type {
  ClassActionLawsuitSearchType,
  ClassActionLawsuitSearchFormData,
} from '../types';

export type { ClassActionLawsuitSearchType, ClassActionLawsuitSearchFormData };

export interface ClassActionLawsuitSearchFormProps {
  onSubmit?: (data: ClassActionLawsuitSearchFormData) => void;
  initialSearchType?: ClassActionLawsuitSearchType;
  initialQuery?: string;
  initialExactMatch?: boolean;
  sortValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const CLASS_ACTION_SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  {
    value: 'keyword',
    label: 'Keyword',
    placeholder: 'Enter keyword',
  },
  {
    value: 'company',
    label: 'Company Name',
    placeholder: 'Enter company',
  },
];

const CLASS_ACTION_FORM_CONTAINER_SX: SxProps<Theme> = (theme) => ({
  borderRadius: theme.spacing(1),
  border: 'none',
  boxShadow: `0 0 0 1px ${theme.palette.divider}`,
  padding: { xs: 2, sm: 2.5 },
  backgroundColor: 'common.white',
});

const CLASS_ACTION_FORM_TITLE_SX: SxProps<Theme> = {
  fontFamily: "'Nunito Sans', sans-serif",
  fontSize: { xs: '1.125rem', sm: '1.25rem' },
  fontWeight: 700,
  lineHeight: { xs: '28px', sm: '32px' },
  marginBottom: 0,
};

export const ClassActionLawsuitSearchForm: React.FC<
  ClassActionLawsuitSearchFormProps
> = ({
  onSubmit,
  initialSearchType = 'keyword',
  initialQuery = '',
  sortValue,
  isLoading = false,
  disabled = false,
}) => {
  const handleSubmit = (data: SearchFormData) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        searchType: data.searchType as ClassActionLawsuitSearchType,
      });
    }
  };

  const initialFilters = sortValue
    ? { moreFilters: { orderBy: sortValue } }
    : undefined;

  return (
    <SearchForm
      title="Search Class Action Lawsuits Across 2000+ Courts"
      searchTypeOptions={CLASS_ACTION_SEARCH_TYPE_OPTIONS}
      onSubmit={handleSubmit}
      initialSearchType={initialSearchType}
      initialQuery={initialQuery}
      initialFilters={initialFilters}
      isLoading={isLoading}
      disabled={disabled}
      showExactMatch={false}
      operatorsData={SEARCH_OPERATORS}
      testId="class-action-lawsuit-search-form"
      formContainerSx={CLASS_ACTION_FORM_CONTAINER_SX}
      titleSx={CLASS_ACTION_FORM_TITLE_SX}
    />
  );
};

export default ClassActionLawsuitSearchForm;
